export type CartState = Record<string, number>;

export const MAX_CART_ITEMS = 10;

export function getCartCount(cart: CartState) {
  return Object.values(cart).reduce((sum, qty) => sum + qty, 0);
}

export function getCartUniqueCount(cart: CartState) {
  return Object.keys(cart).length;
}

export function addToCart(cart: CartState, id: string, delta: number = 1): CartState {
  const nextQty = (cart[id] ?? 0) + delta;
  if (nextQty <= 0) {
    const { [id]: _removed, ...rest } = cart;
    return rest;
  }
  // Enforce "only one per item" rule.
  return { ...cart, [id]: 1 };
}

export function setCartQty(cart: CartState, id: string, qty: number): CartState {
  if (qty <= 0) {
    const { [id]: _removed, ...rest } = cart;
    return rest;
  }
  // Enforce "only one per item" rule.
  return { ...cart, [id]: 1 };
}

export function toggleCartItem(cart: CartState, id: string): CartState {
  if (cart[id]) {
    const { [id]: _removed, ...rest } = cart;
    return rest;
  }
  // Enforce max unique items.
  if (Object.keys(cart).length >= MAX_CART_ITEMS) return cart;
  return { ...cart, [id]: 1 };
}

const STORAGE_KEY = "pantry_parade_cart_v1";

export function loadCart(): CartState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const obj = parsed as Record<string, unknown>;
    const normalized: CartState = {};
    for (const [k, v] of Object.entries(obj)) {
      if (typeof v === "number" && Number.isFinite(v) && v > 0) normalized[k] = Math.floor(v);
    }
    // Enforce max unique items on load as well (in case older storage had more).
    const keys = Object.keys(normalized);
    if (keys.length > MAX_CART_ITEMS) {
      keys.sort();
      const trimmed: CartState = {};
      for (const k of keys.slice(0, MAX_CART_ITEMS)) trimmed[k] = 1;
      return trimmed;
    }
    return normalized;
  } catch {
    return {};
  }
}

export function saveCart(cart: CartState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
  } catch {
    // ignore
  }
}
